import { getRealisation } from "@/lib/content/realisations-bank";

// Construit la liste `{{sections_disponibles}}` injectée dans le prompt des
// messages d'accompagnement (generate-messages), à partir de ce qui est
// RÉELLEMENT présent sur l'offre à cet instant : l'IA n'annonce ainsi que du
// contenu qui existe vraiment. Pas de "server-only" : utilisé aussi bien côté
// client (offre-form.tsx, avec les valeurs du formulaire) que côté serveur
// (run-full-offer.ts, avec `content_items.data` tel quel) — les deux formes
// sont structurellement compatibles avec `OffreData`.
export interface SectionsDisponiblesInput {
  strategicStakes?: { items?: unknown[] } | null;
  stakesMockups?: { images?: unknown[] } | null;
  pricingGrid?: { lines?: { kind?: string }[] } | null;
  loomUrl?: string | null;
  caseStudy?: { realisationId?: string | null } | null;
  contentBlocks?: { title?: string | null }[] | null;
  faq?: { items?: unknown[] } | null;
  hideScoping?: boolean | null;
}

export function buildSectionsDisponibles(input: SectionsDisponiblesInput): string {
  const items: string[] = [
    "Moyens de garder le contrôle : développeur senior en direct, accès live pendant le dev, 100% propriétaire dès J1, documentation continue, back-office d'administration",
    input.hideScoping
      ? "Déroulé de la mission : développement milestone par milestone, 3 mois de support inclus puis maintenance à l'heure"
      : "Déroulé de la mission : cadrage, développement milestone par milestone, 3 mois de support inclus puis maintenance à l'heure",
  ];

  if ((input.strategicStakes?.items?.length ?? 0) > 0) {
    const withMockups = (input.stakesMockups?.images?.length ?? 0) > 0;
    items.push(
      withMockups
        ? "Enjeux stratégiques du projet, chacun illustré par une maquette d'écran"
        : "Enjeux stratégiques du projet (problématique puis solution)"
    );
  }

  const lines = (input.pricingGrid?.lines ?? []) as { kind?: string; amountLow?: number; amountHigh?: number }[];
  if (lines.some((l) => l?.kind === "base")) {
    // Grilles générées depuis le 2026-10-12 : prix fixe découpé par jalon.
    const fixed = lines.every((l) => (l?.amountHigh ?? 0) <= (l?.amountLow ?? 0));
    items.push(fixed ? "Prix fixe du projet, découpé jalon par jalon" : "Grille tarifaire détaillée, fonctionnalité par fonctionnalité");
  }
  if (lines.some((l) => l?.kind === "addon")) {
    items.push("Options à ajouter si besoin, chacune à prix fixe");
  }

  if (input.loomUrl?.trim()) items.push("Vidéo de présentation");
  const realisation = getRealisation(input.caseStudy?.realisationId);
  if (realisation) {
    // Faits réels de la banque de réalisations : preuve citable telle quelle
    // dans le teaser (approche directe, 2026-10-12), jamais à enjoliver.
    const temoin = realisation.testimonial
      ? ` ; témoignage de ${realisation.testimonial.author} (${realisation.testimonial.role})`
      : "";
    items.push(
      `Étude de cas d'un projet Léman IT proche du besoin : ${realisation.name}, ${realisation.title}. Résultat réel : ${realisation.results}${temoin}`
    );
  }
  for (const block of input.contentBlocks ?? []) {
    if (block?.title?.trim()) items.push(block.title.trim());
  }
  if ((input.faq?.items?.length ?? 0) > 0) {
    items.push("FAQ qui répond à vos questions les plus probables");
  }

  return items.map((i) => `- ${i}`).join("\n");
}
